import type { Module } from 'vuex';
import { Backup } from '@/types/Backup';
import {RootState} from "@/store";

export interface BackupType {
  indexBackupType: number;
  backupType: string;
}

export interface BackupsState {
  backups: Backup[];
  backupTypes: BackupType[];
  backupInProgress: boolean;
}

const backupsModule: Module<BackupsState, RootState> = {
  namespaced: true,

  state: (): BackupsState => ({
    backups: [],
    backupTypes: [],
    backupInProgress: false,
  }),

  getters: {
    getByTime: (state) => (time: number): Backup | undefined => {
      return state.backups.find(backup => backup.time === time);
    },
    getBackupType: (state) => (type: number): string => {
      const backupType = state.backupTypes.find(item => item.indexBackupType === type);
      return backupType ? backupType.backupType : 'null';
    },
  },

  mutations: {
    setBackups(
      state,
      payload: { backups: Record<number, any>, backupInProgress?: boolean }
    ) {
      state.backups = Object
        .values(payload.backups)
        .map(raw => Backup.fromRaw(raw));
      state.backupInProgress = payload.backupInProgress ?? false;
    },

    setBackupTypes(
      state,
      payload: { backupTypes: Record<number, string> }
    ) {
      state.backupTypes = Object.keys(payload.backupTypes).map(backupTypeIndex => ({
        indexBackupType: parseInt(backupTypeIndex),
        backupType: payload.backupTypes[parseInt(backupTypeIndex)],
      }));
    },

    setBackupCreate(
      state,
      payload: {
        dir: string;
        time: number;
        name: string | null;
        type: number;
        databaseMain: boolean;
        databasePrice: boolean;
        finished: boolean | null;
      }
    ) {
      const raw = {
        dir: payload.dir,
        time: payload.time,
        name: payload.name,
        type: payload.type,
        databaseMain: payload.databaseMain,
        databasePrice: payload.databasePrice,
        finished: payload.finished,
      };
      state.backups.push(Backup.fromRaw(raw));
      state.backupInProgress = true;
    },

    setBackupCreateSuccess(
      state,
      payload: { time: number }
    ) {
      const backup = state.backups.find(backup => backup.time === payload.time);
      if (backup) {
        // Note: finished is readonly in Backup class, so we need to replace the backup
        const index = state.backups.findIndex(backup => backup.time === payload.time);
        if (index !== -1) {
          const raw = {
            dir: backup.dir,
            time: backup.time,
            name: backup.name,
            type: backup.type,
            databaseMain: backup.databaseMain,
            databasePrice: backup.databasePrice,
            finished: true,
          };
          state.backups[index] = Backup.fromRaw(raw);
        }
      }
      state.backupInProgress = false;
    },

    setBackupCreateError(
      state,
      payload: { time: number }
    ) {
      const backup = state.backups.find(backup => backup.time === payload.time);
      if (backup) {
        const index = state.backups.findIndex(backup => backup.time === payload.time);
        if (index !== -1) {
          const raw = {
            dir: backup.dir,
            time: backup.time,
            name: backup.name,
            type: backup.type,
            databaseMain: backup.databaseMain,
            databasePrice: backup.databasePrice,
            finished: null,
          };
          state.backups[index] = Backup.fromRaw(raw);
        }
      }
      state.backupInProgress = false;
    },

    setBackupDelete(
      state,
      payload: { time: number }
    ) {
      state.backups = state.backups.filter(
        backup => backup.time !== payload.time
      );
    },

    setBackupRestore(
      state,
      payload: { time: number }
    ) {
      const backup = state.backups.find(backup => backup.time === payload.time);
      if (backup) {
        const index = state.backups.findIndex(backup => backup.time === payload.time);
        if (index !== -1) {
          const raw = {
            dir: backup.dir,
            time: backup.time,
            name: backup.name,
            type: backup.type,
            databaseMain: backup.databaseMain,
            databasePrice: backup.databasePrice,
            finished: false,
          };
          state.backups[index] = Backup.fromRaw(raw);
        }
      }
    },

    setBackupRestoreSuccess(
      state,
      payload: { time: number }
    ) {
      const backup = state.backups.find(backup => backup.time === payload.time);
      if (backup) {
        const index = state.backups.findIndex(backup => backup.time === payload.time);
        if (index !== -1) {
          const raw = {
            dir: backup.dir,
            time: backup.time,
            name: backup.name,
            type: backup.type,
            databaseMain: backup.databaseMain,
            databasePrice: backup.databasePrice,
            finished: true,
          };
          state.backups[index] = Backup.fromRaw(raw);
        }
      }
    },

    setBackupRestoreError(
      state,
      payload: { time: number }
    ) {
      const backup = state.backups.find(backup => backup.time === payload.time);
      if (backup) {
        const index = state.backups.findIndex(backup => backup.time === payload.time);
        if (index !== -1) {
          const raw = {
            dir: backup.dir,
            time: backup.time,
            name: backup.name,
            type: backup.type,
            databaseMain: backup.databaseMain,
            databasePrice: backup.databasePrice,
            finished: null,
          };
          state.backups[index] = Backup.fromRaw(raw);
        }
      }
    },
  },

  actions: {
    wsSetBackups({ commit }, payload) {
      commit('setBackups', payload);
    },
    wsSetBackupTypes({ commit }, payload) {
      commit('setBackupTypes', payload);
    },
    wsBackupCreate({ commit }, payload) {
      commit('setBackupCreate', payload);
    },
    wsBackupCreateSuccess({ commit }, payload) {
      commit('setBackupCreateSuccess', payload);
    },
    wsBackupCreateError({ commit }, payload) {
      commit('setBackupCreateError', payload);
    },
    wsBackupDelete({ commit }, payload) {
      commit('setBackupDelete', payload);
    },
    wsBackupRestore({ commit }, payload) {
      commit('setBackupRestore', payload);
    },
    wsBackupRestoreSuccess({ commit }, payload) {
      commit('setBackupRestoreSuccess', payload);
    },
    wsBackupRestoreError({ commit }, payload) {
      commit('setBackupRestoreError', payload);
    },
  },
};

export default backupsModule; 